import Vue from "vue";
import i18n from "@/plugins/i18n";
import router from "@/router";

export default function ({ globals }) {
	return {
		namespaced: true,
		state: {
			isInvitation: false,
			invitationData: {},
			registrationToken: null,
			canRegister: false,
			isTokenOrInvitationLoading: true,
			signUpErrorCode: null,
			emailAddress: "",
			emailProvided: false,
			inlineErrors: {
				emailNotValidError: null,
			},
		},
		mutations: {
			SET_EMAIL_ADDRESS(state, value) {
				state.emailAddress = value ? value.replace(/\s/g, "") : value;
			},
			SET_EMAIL_PROVIDED(state, value) {
				state.emailProvided = value;
			},
			SET_INLINE_ERROR(state, { key, value }) {
				Vue.set(state.inlineErrors, key, value);
			},
			SET_IS_INVITATION(state, value) {
				state.isInvitation = value;
			},
			SET_INVITATION_DATA(state, value) {
				state.invitationData = value;
			},
			SET_REGISTRATION_TOKEN(state, value) {
				state.registrationToken = value;
			},
			SET_CAN_REGISTER(state, value) {
				state.canRegister = value;
			},
			SET_TOKEN_OR_INVITATION_LOADING(state, value) {
				state.isTokenOrInvitationLoading = value;
			},
			SET_SIGNUP_ERROR_CODE(state, value) {
				state.signUpErrorCode = value;
			},
		},
		actions: {
			async validateInvitation({ rootState, commit, dispatch }) {
				try {
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const invitationValidationResponse = await fetch(`${globalConfig.backends.users}/validate-invitation-token/${rootState.queryParams.secret}`, {
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
					});

					const { result, error = null } = await invitationValidationResponse.json();
					if (error) throw new Error(error.code);
					if (!result.invitations || result.invitations.length === 0) throw new Error("invitationNoWorkspace");

					commit("SET_IS_INVITATION", true);
					commit("SET_INVITATION_DATA", result);
					commit("SET_EMAIL_ADDRESS", result.email);
					commit("SET_CAN_REGISTER", true);
					commit("SET_TOKEN_OR_INVITATION_LOADING", false);
				} catch (error) {
					const translationExists = i18n.te(`registrationSignUpComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";
					commit("SET_CAN_REGISTER", false);
					commit("SET_SIGNUP_ERROR_CODE", error.message);
					commit("SET_TOKEN_OR_INVITATION_LOADING", false);

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`registrationSignUpComponent.errorMessages.${code}`),
						target: {
							uid: "registrationSignUpErrors",
							type: "flagToast",
						},
					}, { root: true });
				}
			},
			async validateToken({ rootState, commit, dispatch }) {
				try {
					if (!rootState.queryParams.token) {
						commit("SET_CAN_REGISTER", false);
						commit("SET_TOKEN_OR_INVITATION_LOADING", false);
						commit("SET_SIGNUP_ERROR_CODE", "noToken");
						return;
					}
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const registrationTokenValidationResponse = await fetch(`${globalConfig.backends.users}/validate-registration-token/${rootState.queryParams.token}`, {
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
					});

					const { error = null } = await registrationTokenValidationResponse.json();
					if (error) throw new Error(error.code);

					commit("SET_REGISTRATION_TOKEN", rootState.queryParams.token);
					commit("SET_CAN_REGISTER", true);
					commit("SET_TOKEN_OR_INVITATION_LOADING", false);
				} catch (error) {
					const translationExists = i18n.te(`registrationSignUpComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";
					commit("SET_CAN_REGISTER", false);
					commit("SET_TOKEN_OR_INVITATION_LOADING", false);
					commit("SET_SIGNUP_ERROR_CODE", error.message);

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`registrationSignUpComponent.errorMessages.${code}`),
						target: {
							uid: "registrationSignUpErrors",
							type: "flagToast",
						},
					}, { root: true });
				}
			},
			async checkEmailAvailability({ state, commit, dispatch }) {
				try {
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const emailAvailabilityResponse = await fetch(`${globalConfig.backends.users}/availability/email`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							email: state.emailAddress,
						}),
					});

					const { result, error = null } = await emailAvailabilityResponse.json();
					if (error) throw new Error(error.code);

					if (!result.email && !result.isEmailInvited) {
						throw new Error("emailTaken");
					}

					commit("SET_INLINE_ERROR", { key: "emailNotValidError", value: null });
					return;
				} catch (error) {
					const translationExists = i18n.te(`registrationSignUpComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					if (error.message === "emailTaken") {
						commit("SET_INLINE_ERROR", { key: "emailNotValidError", value: i18n.t("rules.emailTaken") });
					}
					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`registrationSignUpComponent.errorMessages.${code}`),
						target: {
							uid: error.message === "emailTaken" ? null : "registrationSignUpErrors",
							type: error.message === "emailTaken" ? "inline" : "flagToast",
						},
					}, { root: true });
				}
			},
			async registerEmailForConfirmation({ state, commit, rootState, dispatch }) {
				try {
					if (state.inlineErrors.emailNotValidError) return;
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");

					if (state.isInvitation) {
						const response = await fetch(`${globalConfig.backends.users}/register/${state.emailAddress}/accept-invitation`, {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify({ invitationSecret: rootState.queryParams.secret }),
						});

						const { result, error = null } = await response.json();
						if (error) throw new Error(error.code);
						if (result.email === state.emailAddress) {
							commit("setUpAccount/SET_REGISTRATION_EMAIL_CODE", result.code, { root: true });
							commit("setUpAccount/SET_REGISTRATION_EMAIL_CONFIRMATION", true, { root: true });
							commit("setUpAccount/SET_INVITATION_HASH", result.invitationHash, { root: true });
							commit("setUpAccount/SET_USER_WAS_INVITED", true, { root: true });
							commit("setUpAccount/SET_INVITATION_DATA", result, { root: true });
							router.push({ path: "/register/account" }).catch(() => {});
							return;
						} else {
							commit("SET_EMAIL_PROVIDED", true);
						}
					}
					const response = await fetch(`${globalConfig.backends.users}/register/${state.emailAddress}/send`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							registrationToken: state.registrationToken,
						}),
					});

					const { error = null } = await response.json();
					if (error) throw new Error(error.code);
					commit("SET_EMAIL_PROVIDED", true);
				} catch (error) {
					const translationExists = i18n.te(`registrationSignUpComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";
					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`registrationSignUpComponent.errorMessages.${code}`),
						target: {
							uid: "registrationSignUpErrors",
							type: "flagToast",
						},
					}, { root: true });
				}
			},
			async resendRegistrationEmail({ state, rootState, dispatch }) {
				try {
					if (state.inlineErrors.emailNotValidError) return;
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");

					if (state.isInvitation) {
						const response = await fetch(`${globalConfig.backends.users}/register/${state.emailAddress}/accept-invitation`, {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify({ invitationSecret: rootState.queryParams.secret }),
						});

						const { error = null } = await response.json();
						if (error) throw new Error(error.code);

						dispatch("logger/logSuccess", {
							id: Date.now(),
							handled: true,
							code: "confirmationEmailSent",
							source: "backend",
							notification: i18n.t("registrationConfirmationComponent.successMessages.confirmationEmailSent"),
							target: {
								type: "flashToast",
								timeout: 4000,
							},
						}, { root: true });
						return;
					}

					const response = await fetch(`${globalConfig.backends.users}/register/${state.emailAddress}/send`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							registrationToken: state.registrationToken,
						}),
					});

					const { error = null } = await response.json();
					if (error) throw new Error(error.code);

					dispatch("logger/logSuccess", {
						id: Date.now(),
						handled: true,
						code: "confirmationEmailSent",
						source: "backend",
						notification: i18n.t("registrationConfirmationComponent.successMessages.confirmationEmailSent"),
						target: {
							type: "flashToast",
							timeout: 4000,
						},
					}, { root: true });
				} catch (error) {
					const translationExists = i18n.te(`registrationConfirmationComponent.errorMessages.${error.message}`);
					const code = translationExists ? error.message : "networkError";
					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`registrationConfirmationComponent.errorMessages.${code}`),
						target: {
							type: "flashToast",
							timeout: 4000,
						},
					}, { root: true });
				}
			},
		},
	};
}
